// Gates the app behind the session (pattern from cinematic_database).
// On mount asks /api/auth/check — which also slides the 30-day expiry, so a
// tablet that is used at all stays signed in indefinitely. Any API call that
// hits an unexpected 401 flips back to the login screen via the window event.

import { useCallback, useEffect, useState, type ReactNode } from "react";

import { apiFetch, UNAUTHORIZED_EVENT } from "@/api/client";
import { Login } from "@/auth/Login";

type AuthState = "checking" | "authed" | "anon";

export function AuthGuard({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>("checking");

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ username: string }>("/api/auth/check")
      .then(() => !cancelled && setState("authed"))
      .catch(() => !cancelled && setState("anon"));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onUnauthorized = () => setState("anon");
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const onLogin = useCallback(() => setState("authed"), []);

  if (state === "checking") {
    return <div className="flex h-screen items-center justify-center bg-background" />;
  }
  if (state === "anon") {
    return <Login onLogin={onLogin} />;
  }
  return <>{children}</>;
}
